import { c as _c } from "react/compiler-runtime";
import SettingConfig from '@module/config/setting';
import type { MediaPlayer } from 'amazon-ivs-player';
import { useEffect, useState } from 'react';
import type Player from 'video.js/dist/types/player';
import { useFetchLiveUrlMutation } from '../../queries/live/liveQueryHooks';
import { useFetchLivePlayMutationStore } from '../../stores/useFetchLivePlayMutationStore';
import useModalStore from '../../stores/useModalStore';
import useOldPlayerStore from '../../stores/useOldPlayerStore';
import usePlayerStore from '../../stores/usePlayerStore';
import { createBlindOffUpdate } from '../centrifuge/messageUpdates';
const RETRY_COUNT = 7;
/** amazon-ivs-player PlayerState.PLAYING (`getState()` 반환 타입과 동일) — SSR에서 SDK value import 방지 */
const IVS_PLAYER_STATE_PLAYING = 'Playing' as ReturnType<MediaPlayer['getState']>;
const isHTMLVideoElement = (p: unknown): p is HTMLVideoElement => typeof HTMLVideoElement !== 'undefined' && p instanceof HTMLVideoElement;

/**
 * 플레이어가 현재 정상 재생 중인지 판별한다.
 * - RT(HTMLVideoElement): 네이티브 프로퍼티.
 * - Fanza LL(MediaPlayer): getState() === PLAYING.
 * - Panda LL(Player): paused/ended/seeking/readyState 메서드.
 */
function isPlayerPlaying(rtVideo: HTMLVideoElement | null, player: MediaPlayer | Player | null): boolean {
  if (rtVideo) {
    return !rtVideo.paused && !rtVideo.ended && !rtVideo.seeking && rtVideo.readyState >= 3;
  }
  if (!player) return false;
  if ('getState' in player) {
    return player.getState() === IVS_PLAYER_STATE_PLAYING;
  }
  return !player.paused() && !player.ended() && !player.seeking() && player.readyState() >= 3;
}

/**
 * SocketPayload - CastResume
 */
function useCastResume() {
  const $ = _c(21);
  const {
    pushModal
  } = useModalStore();
  const playInfo = useFetchLivePlayMutationStore(_temp);
  const setPlayInfo = useFetchLivePlayMutationStore(_temp2);
  const fanzaPlayer = usePlayerStore(_temp3);
  const oldPlayer = useOldPlayerStore(_temp4);
  const projectPlayer = SettingConfig.isFanza ? fanzaPlayer : oldPlayer;
  let t0;
  if ($[0] !== projectPlayer) {
    t0 = isHTMLVideoElement(projectPlayer) ? projectPlayer : null;
    $[0] = projectPlayer;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const rtVideo = t0;
  let t1;
  if ($[2] !== projectPlayer) {
    t1 = isHTMLVideoElement(projectPlayer) ? null : projectPlayer;
    $[2] = projectPlayer;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const player = t1;
  const [fetchNewUrlCount, setFetchNewUrlCount] = useState(0);
  let t2;
  if ($[4] !== playInfo || $[5] !== player || $[6] !== rtVideo || $[7] !== setPlayInfo) {
    t2 = {
      onSuccess: data => {
        if (rtVideo && playInfo && data.PlayList?.whip?.[0]?.token) {
          setPlayInfo({
            ...playInfo,
            PlayList: data.PlayList
          });
          return;
        }
        if (!player) {
          return;
        }
        const url = data.PlayList.hls[0].url;
        if ("getState" in player) {
          player.load(url);
          player.setMuted(false);
        } else {
          player.src(url);
          player.muted(false);
        }
        player.play();
      },
      retry: false
    };
    $[4] = playInfo;
    $[5] = player;
    $[6] = rtVideo;
    $[7] = setPlayInfo;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    mutate: fetchLiveUrl
  } = useFetchLiveUrlMutation(t2);
  let t3;
  if ($[9] !== fetchLiveUrl || $[10] !== fetchNewUrlCount || $[11] !== playInfo || $[12] !== player || $[13] !== projectPlayer || $[14] !== pushModal || $[15] !== rtVideo || $[16] !== setPlayInfo) {
    t3 = () => {
      if (fetchNewUrlCount < 1 || !projectPlayer) {
        return;
      }
      if (isPlayerPlaying(rtVideo, player) && playInfo) {
        setPlayInfo(createBlindOffUpdate(playInfo));
        setFetchNewUrlCount(0);
        return;
      }
      if (fetchNewUrlCount > RETRY_COUNT) {
        pushModal({
          type: "CONFIRM_CANCEL_MODAL",
          options: {
            description: "\uC2A4\uD2B8\uB9AC\uBC0D \uC5F0\uACB0\uC774 \uC6D0\uD65C\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.\n\uC0C8\uB85C\uACE0\uCE68 \uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
            onClick: _temp5
          }
        });
        return;
      }
      const randomIntervalTime = Math.floor((Math.random() * 7 + 3) * 1000);
      const fetchTimeOut = setTimeout(() => {
        if (!playInfo?.media.code) {
          return;
        }
        fetchLiveUrl({
          mediaCode: playInfo.media.code,
          token: playInfo.chatServer.token,
          t: playInfo.chatServer.t
        });
        setFetchNewUrlCount(_temp6);
      }, randomIntervalTime);
      return () => clearTimeout(fetchTimeOut);
    };
    $[9] = fetchLiveUrl;
    $[10] = fetchNewUrlCount;
    $[11] = playInfo;
    $[12] = player;
    $[13] = projectPlayer;
    $[14] = pushModal;
    $[15] = rtVideo;
    $[16] = setPlayInfo;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== fetchNewUrlCount) {
    t4 = [fetchNewUrlCount];
    $[18] = fetchNewUrlCount;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  useEffect(t3, t4);
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    const startRetry = () => {
      setFetchNewUrlCount(1);
    };
    t5 = {
      startRetry
    };
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}
function _temp6(prev) {
  return prev + 1;
}
function _temp5() {
  window.location.reload();
}
function _temp4(s_0) {
  return s_0.player;
}
function _temp3(s) {
  return s.player;
}
function _temp2(state_0) {
  return state_0.setData;
}
function _temp(state) {
  return state.data;
}
export default useCastResume;